import React, { useState, useEffect, useRef, useCallback } from 'react';
import { TelnyxRTC } from '@telnyx/webrtc';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { getVoiceAudioConstraints, getHDVoiceCodecs, getNoiseFilteredStream } from '../utils/audioQuality';
import { Phone, PhoneOff, Mic, MicOff, Volume2, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * BargeInWebRTC - Connects supervisor to a live call conference via WebRTC.
 * Used when a supervisor barges into an active AI call.
 *
 * Props:
 *  - isOpen: boolean - show/hide dialog
 *  - onClose: function - called when dialog closes
 *  - conferenceId: string - Telnyx conference ID
 *  - conferenceName: string - Telnyx conference name
 *  - callControlId: string - the customer's call control ID
 *  - attemptId: string - the AI call attempt ID
 *  - leadName: string - for display
 */
export default function BargeInWebRTC({
  isOpen,
  onClose,
  conferenceId,
  conferenceName,
  callControlId,
  attemptId,
  leadName = 'Unknown',
}) {
  const [client, setClient] = useState(null);
  const [currentCall, setCurrentCall] = useState(null);
  const [callState, setCallState] = useState('idle'); // idle, connecting, active, ended, error
  const [isMuted, setIsMuted] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [error, setError] = useState(null);

  const remoteAudioRef = useRef(null);
  const callStartTimeRef = useRef(null);
  const durationIntervalRef = useRef(null);
  const rnnoiseCleanupRef = useRef(null);

  // Initialize WebRTC client and connect to conference
  useEffect(() => {
    if (!isOpen || !conferenceId) return;

    let isMounted = true;
    let telnyxClient = null;

    const initAndConnect = async () => {
      try {
        if (!isMounted) return;
        setCallState('connecting');
        setError(null);

        // Check WebRTC support
        if (!window.RTCPeerConnection) {
          throw new Error('WebRTC is not supported in this browser');
        }

        // Get WebRTC credentials
        const credRes = await axios.get(`${API}/telnyx/webrtc-credentials`);
        const cred = credRes.data;

        if (!cred?.sip_username || !cred?.sip_password) {
          throw new Error('WebRTC credentials not available');
        }

        // Create Telnyx client
        telnyxClient = new TelnyxRTC({
          login: cred.sip_username,
          password: cred.sip_password,
          ringtoneFile: null,
          ringbackFile: null,
          prefetchIceCandidates: true,
        });

        telnyxClient.on('telnyx.ready', async () => {
          if (!isMounted) return;

          // Call into the conference SIP address with HD audio
          const sipUri = `sip:${conferenceName}@sip.telnyx.com`;

          const audioConstraints = getVoiceAudioConstraints();
          const preferred_codecs = getHDVoiceCodecs();

          const filtered = await getNoiseFilteredStream(null, true);

          const callOptions = {
            destinationNumber: sipUri,
            callerName: 'Supervisor',
            callerNumber: '',
            video: false,
            useStereo: false,
            ...(preferred_codecs ? { preferred_codecs } : {}),
          };

          if (filtered) {
            callOptions.localStream = filtered.stream;
            rnnoiseCleanupRef.current = filtered.cleanup;
          } else {
            callOptions.audio = audioConstraints;
          }

          const call = telnyxClient.newCall(callOptions);
          setCurrentCall(call);
        });

        telnyxClient.on('telnyx.notification', (notification) => {
          if (!isMounted) return;
          const call = notification.call;

          switch (notification.type) {
            case 'callUpdate':
              if (call.state === 'active') {
                setCallState('active');
                callStartTimeRef.current = Date.now();

                // Set up remote audio
                if (remoteAudioRef.current && call.remoteStream) {
                  remoteAudioRef.current.srcObject = call.remoteStream;
                  remoteAudioRef.current.play().catch(() => {});
                }

                // Enable server-side noise suppression
                const ccId = call.telnyxCallControlId;
                if (ccId) {
                  axios.post(`${API}/telnyx/noise-suppression`, {
                    call_control_id: ccId,
                    direction: 'both',
                  }).catch(() => {});
                }
              } else if (call.state === 'hangup' || call.state === 'destroy') {
                setCallState('ended');
                clearInterval(durationIntervalRef.current);
              }
              break;
            case 'error':
              setError('Connection error');
              setCallState('error');
              break;
            default:
              break;
          }
        });

        telnyxClient.on('telnyx.error', (error) => {
          console.error('Telnyx WebRTC error:', error);
          if (isMounted) {
            setError('WebRTC connection failed');
            setCallState('error');
          }
        });

        await telnyxClient.connect();
        if (isMounted) setClient(telnyxClient);
      } catch (err) {
        console.error('Barge-in init failed:', err);
        if (isMounted) {
          setError(err.message || 'Failed to connect');
          setCallState('error');
        }
      }
    };

    initAndConnect();

    return () => {
      isMounted = false;
      clearInterval(durationIntervalRef.current);
      if (telnyxClient) {
        try {
          telnyxClient.disconnect();
        } catch (e) {
          /* ignore cleanup errors */
        }
      }
      if (rnnoiseCleanupRef.current) {
        rnnoiseCleanupRef.current();
        rnnoiseCleanupRef.current = null;
      }
    };
  }, [isOpen, conferenceId, conferenceName]);

  // Duration timer
  useEffect(() => {
    if (callState === 'active') {
      durationIntervalRef.current = setInterval(() => {
        if (callStartTimeRef.current) {
          setCallDuration(Math.floor((Date.now() - callStartTimeRef.current) / 1000));
        }
      }, 1000);
    }
    return () => clearInterval(durationIntervalRef.current);
  }, [callState]);

  // Mute/Unmute
  const toggleMute = useCallback(() => {
    if (currentCall) {
      if (isMuted) {
        currentCall.unmuteAudio();
      } else {
        currentCall.muteAudio();
      }
      setIsMuted(!isMuted);
    }
  }, [currentCall, isMuted]);

  // Disconnect and unbarge
  const handleDisconnect = useCallback(async () => {
    try {
      // Disconnect WebRTC call
      if (currentCall) {
        currentCall.hangup();
      }
      if (client) {
        client.disconnect();
      }

      // Tell backend to unbarge (restore AI)
      await axios.post(`${API}/call-monitor/call/${attemptId}/unbarge`);
      toast.success('Left barge - AI assistant restarting');
    } catch (err) {
      console.error('Unbarge error:', err);
    } finally {
      setCallState('ended');
      clearInterval(durationIntervalRef.current);
      onClose();
    }
  }, [currentCall, client, attemptId, onClose]);

  const formatDuration = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleDisconnect(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Volume2 className="h-5 w-5 text-orange-500" />
            Barge-In: {leadName}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col items-center py-6 space-y-4">
          {/* Hidden audio element */}
          <audio ref={remoteAudioRef} autoPlay playsInline />

          {/* Status */}
          {callState === 'connecting' && (
            <div className="flex flex-col items-center gap-2 text-slate-500">
              <Loader2 className="h-8 w-8 animate-spin" />
              <p>Connecting to call...</p>
            </div>
          )}

          {callState === 'active' && (
            <>
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-full bg-green-500 animate-pulse" />
                <span className="text-lg font-semibold">Connected</span>
              </div>
              <Badge variant="secondary" className="text-lg px-4 py-1">
                {formatDuration(callDuration)}
              </Badge>
              <p className="text-sm text-muted-foreground">
                You are now speaking directly with the customer. The AI has been paused.
              </p>
            </>
          )}

          {callState === 'error' && (
            <div className="flex flex-col items-center gap-2 text-red-500">
              <AlertTriangle className="h-8 w-8" />
              <p>{error || 'Connection failed'}</p>
            </div>
          )}

          {callState === 'ended' && (
            <div className="flex flex-col items-center gap-2 text-slate-500">
              <PhoneOff className="h-8 w-8" />
              <p>Disconnected</p>
            </div>
          )}

          {/* Controls */}
          <div className="flex gap-3">
            {callState === 'active' && (
              <Button
                variant={isMuted ? 'destructive' : 'outline'}
                size="sm"
                onClick={toggleMute}
              >
                {isMuted ? <MicOff className="h-4 w-4 mr-1" /> : <Mic className="h-4 w-4 mr-1" />}
                {isMuted ? 'Unmute' : 'Mute'}
              </Button>
            )}

            {(callState === 'active' || callState === 'connecting') && (
              <Button variant="destructive" onClick={handleDisconnect}>
                <PhoneOff className="h-4 w-4 mr-1" /> Leave Call
              </Button>
            )}

            {(callState === 'error' || callState === 'ended') && (
              <Button onClick={onClose}>
                Close
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
